import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import {
  Boxes, Save, Loader2, AlertCircle, CheckCircle, Play, Clock, Calculator, Calendar, Search, Truck
} from 'lucide-react'
import { syncApi } from '@/lib/api'

interface StockMinConfig {
  last_calculation: string | null
  last_calculation_stats: {
    products_total?: number
    products_updated: number
    products_skipped: number
    errors: number
    duration_seconds: number
  } | null
}

interface CalculationProgress {
  current: number
  total: number
  status: 'idle' | 'running' | 'completed' | 'error'
  message: string
  updated: number
  skipped: number
  errors: number
}

interface CalculationStatus {
  is_running: boolean
  progress: CalculationProgress
}

type SupplierParams = {
  name?: string
  mode?: 'periodic' | 'jit' | 'container'
  review_days?: number
  lead_days?: number
  service_level?: number
  lot_weeks_cheap?: number
  lot_weeks_expensive?: number
  expensive_cost_threshold?: number
  basket_threshold_eur?: number
  max_cover_weeks?: number
  holding_rate?: number
  catalog_supplier_id?: number | null
  emergency_supplier?: string | null
  emergency_lead_days?: number
  emergency_premium?: number
  emergency_max_weeks?: number
  [k: string]: unknown
}

interface ReplenishmentConfig {
  defaults: SupplierParams
  suppliers: Record<string, SupplierParams>
  global: {
    high_margin_unit_eur: number
    service_level_high_margin: number
    declining_service_level: number
    excluded_families: string[]
  }
  lead_time_stats?: Record<string, { n: number; lead_days_median: number | null; freight_median: number | null }>
}

interface LevelExplain {
  reference: string
  supplier_code: string | null
  family_code: string
  lam_day: number
  units_window: number
  orders_window: number
  window_days: number
  stockout_days: number
  size_med: number
  cost: number
  margin_unit: number
  service_level: number | null
  min_stock: number
  max_stock: number
  reason: string
  current_min: number
  current_max: number
  params?: SupplierParams
}

const NUMERIC_FIELDS: { key: keyof SupplierParams; label: string; step?: number; hint: string }[] = [
  { key: 'review_days', label: 'Cadencia máx. (días)', hint: 'Cada cuántos días como máximo se lanza pedido a este proveedor' },
  { key: 'lead_days', label: 'Plazo (días)', hint: 'Días desde que se pide hasta que la mercancía está disponible' },
  { key: 'service_level', label: 'Nivel servicio', step: 0.01, hint: 'Probabilidad de no romper stock hasta el siguiente pedido (0,85-0,98)' },
  { key: 'lot_weeks_cheap', label: 'Lote barato (sem)', hint: 'Semanas de venta que se piden de golpe en artículos baratos' },
  { key: 'lot_weeks_expensive', label: 'Lote caro (sem)', hint: 'Semanas de venta que se piden de golpe en artículos caros' },
  { key: 'expensive_cost_threshold', label: 'Caro desde (€)', hint: 'Coste a partir del cual un artículo cuenta como caro' },
  { key: 'basket_threshold_eur', label: 'Cesta (€)', hint: 'Importe de portes gratis o pedido mínimo: al llegar, la cesta está lista' },
  { key: 'max_cover_weeks', label: 'Tope cobertura (sem)', hint: 'El máximo nunca supera estas semanas de venta' },
]

const MODES = [
  { value: 'periodic', label: 'Periódico' },
  { value: 'jit', label: 'Bajo demanda (diario)' },
  { value: 'container', label: 'Contenedor' },
]

export default function StockMinimumPage() {
  const queryClient = useQueryClient()

  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
  const [selectedFamily, setSelectedFamily] = useState<string>('')
  const [pollingEnabled, setPollingEnabled] = useState(false)
  const [config, setConfig] = useState<ReplenishmentConfig | null>(null)
  const [newSupplierCode, setNewSupplierCode] = useState('')
  const [explainRef, setExplainRef] = useState('')
  const [explain, setExplain] = useState<LevelExplain | null>(null)
  const [explainLoading, setExplainLoading] = useState(false)
  const [explainError, setExplainError] = useState<string | null>(null)

  const statsQuery = useQuery({
    queryKey: ['stock-min-config'],
    queryFn: async () => {
      const res = await axios.get('/api/family-config/stock-min-config')
      return res.data as StockMinConfig
    }
  })

  const configQuery = useQuery({
    queryKey: ['replenishment-config'],
    queryFn: async () => {
      const res = await axios.get('/api/replenishment/config')
      return res.data as ReplenishmentConfig
    }
  })

  useEffect(() => {
    if (configQuery.data) setConfig(configQuery.data)
  }, [configQuery.data])

  const statusQuery = useQuery({
    queryKey: ['stock-min-calculation-status'],
    queryFn: async () => {
      const res = await axios.get('/api/stock-minimum/calculate/status')
      return res.data as CalculationStatus
    },
    refetchInterval: pollingEnabled ? 1000 : false
  })

  useEffect(() => {
    if (statusQuery.data?.is_running) {
      setPollingEnabled(true)
    } else if (statusQuery.data?.progress.status === 'completed' || statusQuery.data?.progress.status === 'error') {
      setPollingEnabled(false)
      queryClient.invalidateQueries({ queryKey: ['stock-min-config'] })
    }
  }, [statusQuery.data, queryClient])

  const nextRunQuery = useQuery({
    queryKey: ['stock-min-next-run'],
    queryFn: async () => {
      const res = await axios.get('/api/stock-minimum/next-run')
      return res.data as { next_run: string | null }
    }
  })

  const familiesQuery = useQuery({
    queryKey: ['families-for-stock'],
    queryFn: () => syncApi.getFamilies()
  })

  const saveConfigMutation = useMutation({
    mutationFn: async (cfg: ReplenishmentConfig) => {
      const { lead_time_stats: _ignored, ...body } = cfg
      void _ignored
      await axios.put('/api/replenishment/config', body)
    },
    onMutate: () => setSaveStatus('saving'),
    onSuccess: () => {
      setSaveStatus('saved')
      queryClient.invalidateQueries({ queryKey: ['replenishment-config'] })
      setTimeout(() => setSaveStatus('idle'), 2000)
    },
    onError: () => setSaveStatus('error')
  })

  const calculateMutation = useMutation({
    mutationFn: async (familyCode?: string) => {
      const res = await axios.post('/api/stock-minimum/calculate', { family_code: familyCode || null })
      return res.data
    },
    onSuccess: () => {
      setPollingEnabled(true)
      statusQuery.refetch()
    }
  })

  const handleCalculate = () => {
    const familyName = selectedFamily
      ? familiesQuery.data?.families?.find((f) => f.code === selectedFamily)?.name || selectedFamily
      : 'todos los productos'
    if (confirm(`¿Calcular y escribir mínimo y máximo en Factusol para ${selectedFamily ? `la familia "${familyName}"` : 'todos los productos'}? Solo se escriben los artículos que cambian.`)) {
      calculateMutation.mutate(selectedFamily || undefined)
    }
  }

  const updateSupplier = (code: string, key: string, value: unknown) => {
    setConfig((prev) => {
      if (!prev) return prev
      const suppliers = { ...prev.suppliers, [code]: { ...prev.suppliers[code], [key]: value } }
      return { ...prev, suppliers }
    })
  }

  const updateDefaults = (key: string, value: unknown) => {
    setConfig((prev) => prev ? { ...prev, defaults: { ...prev.defaults, [key]: value } } : prev)
  }

  const addSupplier = () => {
    const code = newSupplierCode.trim()
    if (!code || !config) return
    if (config.suppliers[code]) return
    setConfig({ ...config, suppliers: { ...config.suppliers, [code]: { name: `Proveedor ${code}` } } })
    setNewSupplierCode('')
  }

  const removeSupplier = (code: string) => {
    if (!config) return
    if (!confirm(`¿Quitar los parámetros específicos del proveedor ${code}? Pasará a usar los valores por defecto.`)) return
    const suppliers = { ...config.suppliers }
    delete suppliers[code]
    setConfig({ ...config, suppliers })
  }

  const handleExplain = async () => {
    const ref = explainRef.trim().toUpperCase()
    if (!ref) return
    setExplainLoading(true)
    setExplainError(null)
    setExplain(null)
    try {
      const res = await axios.get(`/api/replenishment/levels/${encodeURIComponent(ref)}`)
      setExplain(res.data as LevelExplain)
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } }
      setExplainError(err.response?.data?.detail || 'No se pudo calcular')
    } finally {
      setExplainLoading(false)
    }
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Nunca'
    return new Date(dateStr).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  }

  const numberInput = (value: unknown, onChange: (v: number | null) => void, step = 1, placeholder = '') => (
    <input
      type="number"
      step={step}
      value={value === null || value === undefined ? '' : String(value)}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
      className="w-24 px-2 py-1.5 text-right text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
    />
  )

  if (configQuery.isLoading || statsQuery.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-10 h-10 text-primary-600 animate-spin" />
          <p className="text-slate-500 font-medium">Cargando configuracion...</p>
        </div>
      </div>
    )
  }

  const stats = statsQuery.data
  const isRunning = statusQuery.data?.is_running || calculateMutation.isPending
  const progress = statusQuery.data?.progress
  const supplierCodes = config ? Object.keys(config.suppliers).sort((a, b) => Number(a) - Number(b)) : []

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <span className="p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
          <Boxes className="w-8 h-8 text-orange-600 dark:text-orange-400" />
        </span>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Stock mínimo y máximo</h1>
          <p className="text-slate-500 dark:text-slate-400">
            Punto de pedido y nivel de relleno por artículo, con parámetros por proveedor
          </p>
        </div>
      </div>

      {/* Último cálculo + ejecutar */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
          <div className="flex items-center gap-3 mb-4">
            <Clock className="w-5 h-5 text-primary-500" />
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Último cálculo</h2>
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-300">{formatDate(stats?.last_calculation ?? null)}</p>
          {stats?.last_calculation_stats && (
            <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
              <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                <p className="text-slate-500 dark:text-slate-400">Artículos escritos</p>
                <p className="text-xl font-bold text-slate-900 dark:text-white">{stats.last_calculation_stats.products_updated}</p>
              </div>
              <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                <p className="text-slate-500 dark:text-slate-400">Sin cambios</p>
                <p className="text-xl font-bold text-slate-900 dark:text-white">{stats.last_calculation_stats.products_skipped}</p>
              </div>
              <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                <p className="text-slate-500 dark:text-slate-400">Errores</p>
                <p className={`text-xl font-bold ${stats.last_calculation_stats.errors > 0 ? 'text-danger-600' : 'text-slate-900 dark:text-white'}`}>{stats.last_calculation_stats.errors}</p>
              </div>
              <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                <p className="text-slate-500 dark:text-slate-400">Duración</p>
                <p className="text-xl font-bold text-slate-900 dark:text-white">{Math.round(stats.last_calculation_stats.duration_seconds / 60)} min</p>
              </div>
            </div>
          )}
          <div className="flex items-center gap-2 mt-4 text-sm text-slate-500 dark:text-slate-400">
            <Calendar className="w-4 h-4" />
            Próxima ejecución programada: {nextRunQuery.data?.next_run ? formatDate(nextRunQuery.data.next_run) : 'sin programar'}
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
          <div className="flex items-center gap-3 mb-4">
            <Play className="w-5 h-5 text-primary-500" />
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Ejecutar cálculo</h2>
          </div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Familia</label>
          <select
            value={selectedFamily}
            onChange={(e) => setSelectedFamily(e.target.value)}
            disabled={isRunning}
            className="w-full px-3 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white mb-4"
          >
            <option value="">Todas las familias</option>
            {familiesQuery.data?.families?.map((family) => (
              <option key={family.code} value={family.code}>{family.code} - {family.name}</option>
            ))}
          </select>
          <button
            onClick={handleCalculate}
            disabled={isRunning}
            className="flex items-center gap-2 px-5 py-2.5 bg-orange-600 text-white rounded-xl font-semibold hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calculator className="w-4 h-4" />}
            {isRunning ? 'Calculando...' : 'Calcular y escribir en Factusol'}
          </button>
          {progress && progress.status !== 'idle' && (
            <div className="mt-4">
              <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400 mb-1">
                <span>{progress.message}</span>
                <span>{progress.current}/{progress.total}</span>
              </div>
              <div className="w-full h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                <div
                  className={`h-2 ${progress.status === 'error' ? 'bg-danger-500' : 'bg-orange-500'}`}
                  style={{ width: `${progress.total ? Math.min(100, (100 * progress.current) / progress.total) : 0}%` }}
                />
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Escritos {progress.updated} · sin cambios {progress.skipped} · errores {progress.errors}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Parámetros por proveedor */}
      {config && (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Truck className="w-5 h-5 text-primary-500" />
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Parámetros por proveedor</h2>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newSupplierCode}
                onChange={(e) => setNewSupplierCode(e.target.value)}
                placeholder="Código Factusol"
                className="w-36 px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
              />
              <button onClick={addSupplier} className="px-3 py-2 text-sm bg-slate-100 dark:bg-slate-700 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200">
                Añadir proveedor
              </button>
            </div>
          </div>

          <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
            Mínimo = unidades para aguantar cadencia + plazo con el nivel de servicio (cuantil de Poisson sobre el ritmo real de venta).
            Máximo = mínimo + lote. Los artículos cuyo margen anual no cubre el coste de tener una unidad parada pasan a bajo pedido.
            Un campo vacío usa el valor por defecto.
          </p>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-900">
                <tr>
                  <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">Proveedor</th>
                  <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">Modo</th>
                  {NUMERIC_FIELDS.map((f) => (
                    <th key={String(f.key)} className="text-right px-3 py-2 text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase" title={f.hint}>{f.label}</th>
                  ))}
                  <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase" title="Proveedor de emergencia para modo contenedor">Emergencia</th>
                  <th className="text-right px-3 py-2 text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase" title="Plazo real medido con el registro de pedidos">Plazo medido</th>
                  <th></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                <tr className="bg-orange-50/50 dark:bg-orange-900/10">
                  <td className="px-3 py-2 font-medium text-slate-900 dark:text-white">Por defecto</td>
                  <td className="px-3 py-2">
                    <select value={String(config.defaults.mode || 'periodic')} onChange={(e) => updateDefaults('mode', e.target.value)}
                      className="px-2 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white">
                      {MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                  </td>
                  {NUMERIC_FIELDS.map((f) => (
                    <td key={String(f.key)} className="px-3 py-2 text-right">
                      {numberInput(config.defaults[f.key], (v) => updateDefaults(String(f.key), v ?? 0), f.step)}
                    </td>
                  ))}
                  <td className="px-3 py-2 text-slate-400">—</td>
                  <td className="px-3 py-2 text-right text-slate-400">—</td>
                  <td></td>
                </tr>
                {supplierCodes.map((code) => {
                  const sp = config.suppliers[code]
                  const lt = config.lead_time_stats?.[code]
                  return (
                    <tr key={code}>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          value={sp.name || ''}
                          onChange={(e) => updateSupplier(code, 'name', e.target.value)}
                          className="w-40 px-2 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                        />
                        <span className="ml-2 font-mono text-xs text-slate-400">#{code}</span>
                      </td>
                      <td className="px-3 py-2">
                        <select value={String(sp.mode || '')} onChange={(e) => updateSupplier(code, 'mode', e.target.value || null)}
                          className="px-2 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white">
                          <option value="">(defecto)</option>
                          {MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                        </select>
                      </td>
                      {NUMERIC_FIELDS.map((f) => (
                        <td key={String(f.key)} className="px-3 py-2 text-right">
                          {numberInput(sp[f.key], (v) => updateSupplier(code, String(f.key), v), f.step, String(config.defaults[f.key] ?? ''))}
                        </td>
                      ))}
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1">
                          <input
                            type="text"
                            value={sp.emergency_supplier || ''}
                            placeholder="cód."
                            onChange={(e) => updateSupplier(code, 'emergency_supplier', e.target.value || null)}
                            className="w-14 px-2 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                            title="Código Factusol del proveedor de emergencia"
                          />
                          {numberInput(sp.emergency_lead_days, (v) => updateSupplier(code, 'emergency_lead_days', v), 1, 'plazo')}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right text-xs text-slate-500 dark:text-slate-400">
                        {lt?.lead_days_median != null ? `${lt.lead_days_median} d (${lt.n})` : '—'}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button onClick={() => removeSupplier(code)} className="text-xs text-slate-400 hover:text-danger-600">Quitar</button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6 pt-6 border-t border-slate-200 dark:border-slate-700 text-sm">
            <div>
              <label className="block text-slate-700 dark:text-slate-300 mb-1">Coste de tener stock (tasa anual)</label>
              {numberInput(config.defaults.holding_rate, (v) => updateDefaults('holding_rate', v ?? 0.45), 0.01)}
              <p className="text-xs text-slate-500 mt-1">Capital + manipulación + riesgo de obsolescencia. 0,45 = 45 %.</p>
            </div>
            <div>
              <label className="block text-slate-700 dark:text-slate-300 mb-1">Nivel de servicio en alto margen</label>
              <div className="flex items-center gap-2">
                {numberInput(config.global.service_level_high_margin, (v) => setConfig({ ...config, global: { ...config.global, service_level_high_margin: v ?? 0.95 } }), 0.01)}
                <span className="text-xs text-slate-500">si margen/ud ≥</span>
                {numberInput(config.global.high_margin_unit_eur, (v) => setConfig({ ...config, global: { ...config.global, high_margin_unit_eur: v ?? 15 } }), 1)}
                <span className="text-xs text-slate-500">€</span>
              </div>
            </div>
            <div>
              <label className="block text-slate-700 dark:text-slate-300 mb-1">Nivel de servicio si va a la baja</label>
              {numberInput(config.global.declining_service_level, (v) => setConfig({ ...config, global: { ...config.global, declining_service_level: v ?? 0.85 } }), 0.01)}
              <p className="text-xs text-slate-500 mt-1">Último trimestre por debajo de la mitad del anterior.</p>
            </div>
          </div>

          <div className="flex items-center justify-between mt-6 pt-6 border-t border-slate-200 dark:border-slate-700">
            <div className="flex items-center gap-2 text-sm">
              {saveStatus === 'saving' && <span className="text-primary-500 flex items-center gap-1"><Loader2 className="w-4 h-4 animate-spin" /> Guardando...</span>}
              {saveStatus === 'saved' && <span className="text-success-500 flex items-center gap-1"><CheckCircle className="w-4 h-4" /> Guardado</span>}
              {saveStatus === 'error' && <span className="text-danger-500 flex items-center gap-1"><AlertCircle className="w-4 h-4" /> Error al guardar</span>}
              <span className="text-xs text-slate-400 ml-2">Los cambios se aplican en el siguiente cálculo.</span>
            </div>
            <button
              onClick={() => config && saveConfigMutation.mutate(config)}
              disabled={saveConfigMutation.isPending}
              className="flex items-center gap-2 px-6 py-2.5 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Save className="w-4 h-4" />
              Guardar parámetros
            </button>
          </div>
        </div>
      )}

      {/* Explicar una referencia */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
        <div className="flex items-center gap-3 mb-4">
          <Search className="w-5 h-5 text-primary-500" />
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Explicar una referencia</h2>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={explainRef}
            onChange={(e) => setExplainRef(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleExplain()}
            placeholder="Ej: BAT-HP189"
            className="w-60 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
          />
          <button onClick={handleExplain} disabled={explainLoading} className="px-4 py-2 bg-slate-100 dark:bg-slate-700 rounded-lg text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600 disabled:opacity-50">
            {explainLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Calcular'}
          </button>
        </div>
        {explainError && <p className="text-sm text-danger-600 mt-3">{explainError}</p>}
        {explain && (
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded-lg"><p className="text-slate-500">Venta al mes (ritmo real)</p><p className="text-lg font-bold text-slate-900 dark:text-white">{(explain.lam_day * 30.4).toFixed(2)}</p></div>
            <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded-lg"><p className="text-slate-500">Ventas en ventana</p><p className="text-lg font-bold text-slate-900 dark:text-white">{explain.units_window} uds / {explain.orders_window} días · {explain.window_days} d</p></div>
            <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded-lg"><p className="text-slate-500">Días en rotura (inferidos)</p><p className="text-lg font-bold text-slate-900 dark:text-white">{explain.stockout_days}</p></div>
            <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded-lg"><p className="text-slate-500">Coste / margen por ud</p><p className="text-lg font-bold text-slate-900 dark:text-white">{explain.cost.toFixed(2)} € / {explain.margin_unit.toFixed(2)} €</p></div>
            <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg"><p className="text-slate-500">Mínimo (hoy → nuevo)</p><p className="text-lg font-bold text-orange-700 dark:text-orange-300">{explain.current_min} → {explain.min_stock}</p></div>
            <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg"><p className="text-slate-500">Máximo (hoy → nuevo)</p><p className="text-lg font-bold text-orange-700 dark:text-orange-300">{explain.current_max} → {explain.max_stock}</p></div>
            <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded-lg col-span-2"><p className="text-slate-500">Motivo</p><p className="text-slate-900 dark:text-white">{explain.reason}</p><p className="text-xs text-slate-400 mt-1">Proveedor {explain.params?.name || explain.supplier_code || '—'} · familia {explain.family_code} · p={explain.service_level ?? '—'}</p></div>
          </div>
        )}
      </div>
    </div>
  )
}
